// Contacts (UX spec 6.4): search by name/company or exact number, create and edit
// with expected_version. Nothing is seeded; an empty workspace shows the empty state.
import type { Contact } from '@bcio-voice/contracts';
import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, Phone, Plus, Trash2 } from 'lucide-react';
import { Icon } from '../../ui/Icon.tsx';
import { ErrorPanel, SkeletonRows, StatePanel } from '../../ui/StatePanel.tsx';
import { useWorkspace } from '../../state/workspace.tsx';
import { Link, useRouter } from '../../lib/router.tsx';
import { BlocklistClient, type CollectionEnvelope, type DataEnvelope, isAbortError, isApiRequestError, query } from '../../lib/api.ts';
import { describeError, type ErrorView } from '../../lib/errorCopy.ts';
import { formatPhone, parsePhoneInput } from '../../lib/format.ts';
import { hasPermission } from '../../lib/capabilities.ts';
import { useAnnouncer } from '../../ui/Announcer.tsx';
import { type ContactDraft, type ContactErrors, draftFromContact, emptyDraft, validateContact } from './contactForm.ts';
import { NumberStatus } from '../blocklist/NumberStatus.tsx';
import {PhoneNumberActions} from '../../ui/PhoneNumberActions.tsx';
import { blocklistAccess } from '../blocklist/model.ts';
import { useBackgroundRefresh } from '../../state/refreshActivity.ts';

export function ContactsPage({ contactId, creating }: { contactId: string | null; creating: boolean }) {
  const ws = useWorkspace();
  if (!hasPermission(ws.permissions, 'contacts:read')) {
    return (
      <div className="page">
        <h1 className="page-title">Contacts</h1>
        <StatePanel variant="denied" title="You don't have access to contacts" body="Your role doesn't include contacts. Ask an admin if you need it." details={{ permission: 'contacts:read' }} />
      </div>
    );
  }
  return <ContactsWorkspace contactId={contactId} creating={creating} />;
}

interface ListState {
  items: Contact[];
  next: string | null;
  loading: boolean;
  error: ErrorView | null;
  loaded: boolean;
}

function ContactsWorkspace({ contactId, creating }: { contactId: string | null; creating: boolean }) {
  const ws = useWorkspace();
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [list, setList] = useState<ListState>({ items: [], next: null, loading: true, error: null, loaded: false });
  const request = useRef(0);
  const canWrite = hasPermission(ws.permissions, 'contacts:write');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(t);
  }, [term]);

  const load = useCallback((cursor: string | null) => {
    const id = ++request.current;
    const phone = parsePhoneInput(debounced);
    const params = phone.ok ? { phone: phone.e164 } : { q: debounced.slice(0, 100) };
    // Once a list is on screen its last error stays until this load settles: clearing it first would
    // flash "No contacts yet" between a failure and the retry's answer.
    setList(prev => ({ ...prev, loading: true, error: prev.loaded ? prev.error : null }));
    ws.api.request<CollectionEnvelope<Contact>>('/contacts' + query({ ...params, limit: 50, cursor })).then(
      env => {
        if (id !== request.current) return;
        setList(prev => ({ items: cursor ? [...prev.items, ...env.data] : env.data, next: env.page.next_cursor, loading: false, error: null, loaded: true }));
      },
      error => {
        if (id !== request.current || isAbortError(error)) return;
        setList(prev => ({ ...prev, loading: false, error: describeError(error, "Couldn't load contacts"), loaded: true }));
      },
    );
  }, [ws.api, debounced]);

  useEffect(() => { load(null); }, [load]);
  // A new search over a list already shown keeps the old rows and shows the masthead's refresh bar.
  useBackgroundRefresh(list.loaded && list.loading);

  const onSaved = (c: Contact) => setList(prev => ({ ...prev, items: prev.items.some(x => x.id === c.id) ? prev.items.map(x => (x.id === c.id ? c : x)) : [c, ...prev.items] }));
  const onDeleted = (id: string) => setList(prev => ({ ...prev, items: prev.items.filter(x => x.id !== id) }));
  const showDetail = creating || !!contactId;

  return (
    <div className={`split${showDetail ? ' has-detail' : ''}`}>
      <section className="list-pane" aria-labelledby="contacts-title">
        <div className="pane-head">
          <h1 id="contacts-title" className="page-title">Contacts</h1>
          {canWrite ? <Link to="/contacts/new" className="btn btn-primary"><Icon icon={Plus} /> Add contact</Link> : null}
        </div>
        <div className="field search-field">
          <label htmlFor="contact-search">Search by name, company or number</label>
          <input id="contact-search" type="search" value={term} onChange={e => setTerm(e.target.value)} autoComplete="off" />
        </div>
        {list.error && list.items.length === 0 ? <ErrorPanel error={list.error} onRetry={() => load(null)} compact /> : null}
        {!list.loaded ? <SkeletonRows label="Loading contacts" /> : null}
        {list.loaded && !list.error && list.items.length === 0 ? (
          debounced ? (
            <StatePanel variant="filtered" compact title={`No contacts match “${debounced}”`} action={<button type="button" className="btn" onClick={() => setTerm('')}>Clear search</button>} />
          ) : (
            <StatePanel variant="empty" compact title="No contacts yet" body="Contacts appear when you add them or connect the CRM." action={canWrite ? <Link to="/contacts/new" className="btn">Add contact</Link> : undefined} />
          )
        ) : null}
        {list.items.length > 0 ? (
          <ul className="rows" aria-label="Contacts">
            {list.items.map(c => (
              <li key={c.id} className="contact-ledger-item">
                <Link to={`/contacts/${encodeURIComponent(c.id)}`} className={`row${c.id === contactId ? ' is-selected' : ''}`} aria-current={c.id === contactId ? 'page' : undefined}>
                  <span className="row-line1"><span className="row-name">{c.display_name}</span>{c.source === 'crm' ? <span className="row-badge">CRM</span> : null}</span>
                  <span className="row-line2">{c.company_name ? <span>{c.company_name}</span> : null}{c.phones[0] ? <>{c.company_name ? <span className="sep" aria-hidden="true" /> : null}<span className="num">{formatPhone(c.phones[0].e164)}</span></> : null}</span>
                </Link>
                {c.phones[0]?<div className="contact-number-tools"><PhoneNumberActions number={c.phones[0].e164} contactId={c.id} showNumber={false} noteSeed={`Blocked from contact ${c.display_name}.`}/></div>:null}
              </li>
            ))}
          </ul>
        ) : null}
        {list.next ? <div className="list-foot"><button type="button" className="btn" onClick={() => load(list.next)} disabled={list.loading}>{list.loading ? 'Loading…' : 'Load more contacts'}</button></div> : null}
      </section>
      <section className="detail-pane" aria-label="Contact detail">
        {creating ? (
          canWrite ? <ContactEditor key="new" contact={null} onSaved={onSaved} /> : <StatePanel variant="denied" title="You can't add contacts" details={{ permission: 'contacts:write' }} />
        ) : contactId ? (
          <ContactDetail key={contactId} contactId={contactId} canWrite={canWrite} onSaved={onSaved} onDeleted={onDeleted} />
        ) : (
          <StatePanel variant="empty" title="Select a contact" body="Choose a contact to see their numbers, or add a new one." />
        )}
      </section>
    </div>
  );
}

function ContactDetail({ contactId, canWrite, onSaved, onDeleted }: { contactId: string; canWrite: boolean; onSaved: (c: Contact) => void; onDeleted: (id: string) => void }) {
  const ws = useWorkspace();
  const [state, setState] = useState<{ contact: Contact | null; error: ErrorView | null; loading: boolean }>({ contact: null, error: null, loading: true });
  const [editing, setEditing] = useState(false);
  const { navigate } = useRouter();
  const { announce } = useAnnouncer();
  const blocking = blocklistAccess(ws.permissions);
  const blocklist = useMemo(() => new BlocklistClient(ws.api), [ws.api]);
  const reload = useCallback(() => {
    const controller = new AbortController();
    setState(s => ({ ...s, loading: true, error: null }));
    ws.api.request<DataEnvelope<Contact>>(`/contacts/${encodeURIComponent(contactId)}`, { signal: controller.signal }).then(
      env => setState({ contact: env.data, error: null, loading: false }),
      error => { if (!isAbortError(error)) setState({ contact: null, error: describeError(error, "Couldn't load this contact"), loading: false }); },
    );
    return controller;
  }, [ws.api, contactId]);
  useEffect(() => { const c = reload(); return () => c.abort(); }, [reload]);

  const remove = async () => {
    if (!state.contact) return;
    if (!window.confirm(`Delete ${state.contact.display_name}? This can't be undone.`)) return;
    try {
      await ws.api.request<void>(`/contacts/${encodeURIComponent(state.contact.id)}${query({ expected_version: state.contact.version })}`, { method: 'DELETE' });
      onDeleted(state.contact.id);
      announce('Contact deleted');
      navigate('/contacts');
    } catch (error) {
      setState(s => ({ ...s, error: describeError(error, "Couldn't delete this contact") }));
    }
  };

  if (state.loading && !state.contact) return <SkeletonRows rows={3} label="Loading contact" />;
  if (!state.contact) return state.error ? <ErrorPanel error={state.error} onRetry={reload} /> : null;
  const c = state.contact;
  if (editing) return <ContactEditor contact={c} onSaved={saved => { setState({ contact: saved, error: null, loading: false }); onSaved(saved); setEditing(false); }} onCancel={() => setEditing(false)} />;
  const crm = c.source === 'crm';
  return (
    <article className="detail" aria-labelledby="contact-title">
      <Link to="/contacts" className="btn btn-quiet back-link"><Icon icon={ChevronLeft} /> Back to contacts</Link>
      <h2 id="contact-title" className="detail-title">{c.display_name}</h2>
      {c.company_name ? <p className="meta">{c.company_name}</p> : null}
      {crm ? <p className="notice notice-info">Managed in CRM. These fields are read-only here.</p> : null}
      {state.error ? <p className="field-error" role="alert">{state.error.title}{state.error.requestId ? ` Reference ${state.error.requestId}.` : ''}</p> : null}
      <h3 className="section-title">Phone numbers</h3>
      {c.phones.length === 0 ? <p className="field-hint">No phone numbers.</p> : (
        <ul className="plain-list">
          {c.phones.map(p => (
            <li key={p.e164}>
              <div className="phone-line">
                <PhoneNumberActions number={p.e164} contactId={c.id} noteSeed={`Blocked from contact ${c.display_name}.`}/><span className="meta">{p.label}</span>
              </div>
              {blocking.read ? <NumberStatus showControl={false} e164={p.e164} client={blocklist} access={blocking} noteSeed={`Blocked from the contact ${c.display_name}.`} label={`Blocked list status for ${formatPhone(p.e164)}`} /> : null}
            </li>
          ))}
        </ul>
      )}
      <h3 className="section-title">Email</h3>
      {c.emails.length === 0 ? <p className="field-hint">No email addresses.</p> : <ul className="plain-list">{c.emails.map(e => <li key={e}>{e}</li>)}</ul>}
      {canWrite && !crm ? (
        <div className="form-actions">
          <button type="button" className="btn btn-quiet" onClick={() => void remove()}><Icon icon={Trash2} /> Delete</button>
          <button type="button" className="btn btn-primary" onClick={() => setEditing(true)}>Edit contact</button>
        </div>
      ) : null}
    </article>
  );
}

function ContactEditor({ contact, onSaved, onCancel }: { contact: Contact | null; onSaved: (c: Contact) => void; onCancel?: () => void }) {
  const ws = useWorkspace();
  const { navigate } = useRouter();
  const { announce } = useAnnouncer();
  const [draft, setDraft] = useState<ContactDraft>(() => (contact ? draftFromContact(contact) : emptyDraft()));
  const [errors, setErrors] = useState<ContactErrors>({});
  const [serverError, setServerError] = useState<ErrorView | null>(null);
  const [conflict, setConflict] = useState<Contact | null>(null);
  const [version, setVersion] = useState(contact?.version ?? 0);
  const [busy, setBusy] = useState(false);
  const firstRef = useRef<HTMLInputElement>(null);
  useEffect(() => { firstRef.current?.focus(); }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    const result = validateContact(draft);
    if (!result.ok) {
      setErrors(result.errors);
      const first = Object.keys(result.errors)[0];
      if (first) document.getElementById(`cf-${first.replace(/\./g, '-')}`)?.focus();
      return;
    }
    setErrors({});
    setServerError(null);
    setBusy(true);
    try {
      const env = contact
        ? await ws.api.send<DataEnvelope<Contact>>('PATCH', `/contacts/${encodeURIComponent(contact.id)}`, { ...result.fields, expected_version: version })
        : await ws.api.send<DataEnvelope<Contact>>('POST', '/contacts', result.fields);
      announce(contact ? 'Contact saved' : 'Contact added');
      onSaved(env.data);
      setConflict(null);
      if (!contact) navigate(`/contacts/${encodeURIComponent(env.data.id)}`, { replace: true });
    } catch (error) {
      setServerError(describeError(error, "Couldn't save this contact"));
      if (contact && isApiRequestError(error) && error.code === 'version_conflict') {
        try {
          const latest = await ws.api.request<DataEnvelope<Contact>>(`/contacts/${encodeURIComponent(contact.id)}`);
          setConflict(latest.data);
        } catch {
          setConflict(null);
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const setPhone = (i: number, patch: Partial<ContactDraft['phones'][number]>) => setDraft(d => ({ ...d, phones: d.phones.map((p, j) => (j === i ? { ...p, ...patch } : p)) }));
  const err = (key: string) => errors[key];
  const describedBy = (key: string) => (errors[key] ? `cf-${key.replace(/\./g, '-')}-err` : undefined);

  return (
    <form className="detail contact-form" onSubmit={submit} noValidate aria-labelledby="cf-title">
      <h2 id="cf-title" className="detail-title">{contact ? `Edit ${contact.display_name}` : 'New contact'}</h2>
      {conflict ? (
        <div className="notice notice-warn" role="alert">
          <p><strong>Someone else changed this contact</strong> while you were editing. Latest name: {conflict.display_name}.</p>
          <div className="form-actions">
            <button type="button" className="btn" onClick={() => { setDraft(draftFromContact(conflict)); setVersion(conflict.version); setConflict(null); setServerError(null); }}>Use theirs</button>
            <button type="button" className="btn btn-primary" onClick={() => { setVersion(conflict.version); setConflict(null); setServerError(null); }}>Keep mine</button>
          </div>
        </div>
      ) : serverError ? (
        <p className="form-error" role="alert">{serverError.title}{serverError.detail ? ` ${serverError.detail}` : ''}{serverError.requestId ? ` Reference ${serverError.requestId}.` : ''}</p>
      ) : null}
      <div className="field">
        <label htmlFor="cf-display_name">Name</label>
        <input ref={firstRef} id="cf-display_name" value={draft.display_name} maxLength={200} onChange={e => setDraft(d => ({ ...d, display_name: e.target.value }))} aria-invalid={!!err('display_name') || undefined} aria-describedby={describedBy('display_name')} autoComplete="off" required />
        {err('display_name') ? <p id="cf-display_name-err" className="field-error">{err('display_name')}</p> : null}
      </div>
      <div className="field">
        <label htmlFor="cf-company_name">Company <span className="optional">(optional)</span></label>
        <input id="cf-company_name" value={draft.company_name} maxLength={200} onChange={e => setDraft(d => ({ ...d, company_name: e.target.value }))} aria-invalid={!!err('company_name') || undefined} aria-describedby={describedBy('company_name')} autoComplete="off" />
        {err('company_name') ? <p id="cf-company_name-err" className="field-error">{err('company_name')}</p> : null}
      </div>
      <fieldset className="field-group">
        <legend>Phone numbers</legend>
        {draft.phones.map((p, i) => (
          <div className="phone-edit" key={i}>
            <div className="field">
              <label htmlFor={`cf-phones-${i}`}>Number {i + 1}</label>
              <input id={`cf-phones-${i}`} inputMode="tel" value={p.input} onChange={e => setPhone(i, { input: e.target.value })} aria-invalid={!!err(`phones.${i}`) || undefined} aria-describedby={describedBy(`phones.${i}`)} autoComplete="off" />
              {err(`phones.${i}`) ? <p id={`cf-phones-${i}-err`} className="field-error">{err(`phones.${i}`)}</p> : null}
            </div>
            <div className="field">
              <label htmlFor={`cf-phones-${i}-label`}>Label</label>
              <input id={`cf-phones-${i}-label`} value={p.label} maxLength={40} onChange={e => setPhone(i, { label: e.target.value })} aria-invalid={!!err(`phones.${i}.label`) || undefined} aria-describedby={describedBy(`phones.${i}.label`)} />
              {err(`phones.${i}.label`) ? <p id={`cf-phones-${i}-label-err`} className="field-error">{err(`phones.${i}.label`)}</p> : null}
            </div>
            <button type="button" className="btn btn-quiet btn-small" onClick={() => setDraft(d => ({ ...d, phones: d.phones.filter((_, j) => j !== i) }))} aria-label={`Remove number ${i + 1}`}>Remove</button>
          </div>
        ))}
        {err('phones') ? <p className="field-error">{err('phones')}</p> : null}
        {draft.phones.length < 20 ? <button type="button" className="btn btn-small" onClick={() => setDraft(d => ({ ...d, phones: [...d.phones, { input: '', label: 'Mobile' }] }))}><Icon icon={Plus} /> Add number</button> : null}
      </fieldset>
      <fieldset className="field-group">
        <legend>Email addresses</legend>
        {draft.emails.map((e, i) => (
          <div className="field field-inline" key={i}>
            <label htmlFor={`cf-emails-${i}`}>Email {i + 1}</label>
            <input id={`cf-emails-${i}`} type="email" value={e} onChange={ev => setDraft(d => ({ ...d, emails: d.emails.map((x, j) => (j === i ? ev.target.value : x)) }))} aria-invalid={!!err(`emails.${i}`) || undefined} aria-describedby={describedBy(`emails.${i}`)} autoComplete="off" />
            <button type="button" className="btn btn-quiet btn-small" onClick={() => setDraft(d => ({ ...d, emails: d.emails.filter((_, j) => j !== i) }))} aria-label={`Remove email ${i + 1}`}>Remove</button>
            {err(`emails.${i}`) ? <p id={`cf-emails-${i}-err`} className="field-error">{err(`emails.${i}`)}</p> : null}
          </div>
        ))}
        {draft.emails.length < 20 ? <button type="button" className="btn btn-small" onClick={() => setDraft(d => ({ ...d, emails: [...d.emails, ''] }))}><Icon icon={Plus} /> Add email</button> : null}
      </fieldset>
      <div className="form-actions">
        {onCancel ? <button type="button" className="btn" onClick={onCancel}>Cancel</button> : <Link to="/contacts" className="btn">Cancel</Link>}
        <button type="submit" className="btn btn-primary" disabled={busy || !!conflict} aria-busy={busy || undefined}>{busy ? 'Saving…' : 'Save contact'}</button>
      </div>
    </form>
  );
}
